import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, Area, AreaChart,
} from "recharts";
import { Calendar, RefreshCw } from "lucide-react";
import { api, FacilityTimeseries } from "../lib/api";

const PKG_COLORS = ["#2c5282", "#38a169", "#d69e2e", "#dd6b20", "#805ad5", "#319795"];

export function FacilityCharts({ edrpou }: { edrpou: string }) {
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const { data, isLoading, error } = useQuery({
    queryKey: ["facility-timeseries", edrpou, dateFrom, dateTo],
    queryFn: () => {
      const params = new URLSearchParams();
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      return api.get<FacilityTimeseries>(
        `/api/v1/facilities/${edrpou}/timeseries?${params}`
      );
    },
  });

  const packageKeys = useMemo(
    () => (data ? Object.keys(data.package_names).map((id) => `package_${id}`) : []),
    [data]
  );

  if (isLoading) return <div style={{ padding: 24 }}>Завантаження графіків…</div>;
  if (error) return <div style={{ color: "#c53030", padding: 24 }}>Помилка: {(error as any).detail}</div>;
  if (!data) return null;

  const noData =
    data.services_monthly.length === 0 &&
    data.payments_monthly.length === 0 &&
    data.referrals_monthly.length === 0 &&
    data.finance_quarterly.length === 0;

  return (
    <div>
      {/* Date range picker */}
      <div style={{
        background: "white", padding: 14, border: "1px solid #e2e8f0",
        borderRadius: 6, marginBottom: 16, display: "flex", alignItems: "center",
        gap: 12, flexWrap: "wrap",
      }}>
        <Calendar size={16} color="#4a5568" />
        <span style={{ fontSize: 13, color: "#4a5568" }}>Період:</span>
        <input
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          min={data.available_range.earliest || undefined}
          max={data.available_range.latest || undefined}
          style={inputStyle}
        />
        <span style={{ color: "#a0aec0" }}>—</span>
        <input
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          min={data.available_range.earliest || undefined}
          max={data.available_range.latest || undefined}
          style={inputStyle}
        />
        <button
          onClick={() => { setDateFrom(""); setDateTo(""); }}
          style={{
            padding: "6px 10px", background: "white",
            border: "1px solid #cbd5e0", borderRadius: 4,
            cursor: "pointer", fontSize: 12, color: "#4a5568",
            display: "inline-flex", alignItems: "center", gap: 4,
          }}
        >
          <RefreshCw size={11} /> Скинути
        </button>
        <span style={{ marginLeft: "auto", fontSize: 11, color: "#a0aec0" }}>
          Доступно: {data.available_range.earliest || "—"} — {data.available_range.latest || "—"}
        </span>
      </div>

      {noData && (
        <div style={{
          padding: 40, background: "white", border: "1px dashed #cbd5e0",
          borderRadius: 6, textAlign: "center", color: "#718096",
        }}>
          Немає даних для побудови графіків у вибраному діапазоні.
        </div>
      )}

      {/* 1. Послуги per місяць */}
      {data.services_monthly.length > 0 && (
        <ChartCard
          title="Послуги і епізоди по місяцях"
          subtitle={`${data.services_monthly.length} місяців даних · з PBI dashboard НСЗУ`}
        >
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={data.services_monthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="#edf2f7" />
              <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip labelFormatter={fmtDate} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="services" stroke="#2c5282" strokeWidth={2}
                    name="Послуги" dot={{ r: 2 }} />
              <Line type="monotone" dataKey="episodes" stroke="#dd6b20" strokeWidth={2}
                    name="Епізоди" dot={{ r: 2 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      {/* 2. Послуги per пакет (stacked) */}
      {data.services_by_package.length > 0 && packageKeys.length > 0 && (
        <ChartCard
          title="Послуги по пакетах ПМГ (stacked)"
          subtitle={`6 фокусних пакетів · накопичено за ${data.services_by_package.length} місяців`}
        >
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={data.services_by_package}>
              <CartesianGrid strokeDasharray="3 3" stroke="#edf2f7" />
              <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip labelFormatter={fmtDate} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {packageKeys.map((pkg, i) => (
                <Area
                  key={pkg}
                  type="monotone"
                  dataKey={pkg}
                  stackId="1"
                  stroke={PKG_COLORS[i % PKG_COLORS.length]}
                  fill={PKG_COLORS[i % PKG_COLORS.length]}
                  fillOpacity={0.7}
                  name={data.package_names[pkg.replace("package_", "")] || pkg}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      {/* 3. Виплати ПМГ */}
      {data.payments_monthly.length > 0 && (
        <ChartCard
          title="Виплати ПМГ по місяцях"
          subtitle={`${data.payments_monthly.length} платежних періодів · з даних ПМГ НСЗУ`}
        >
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.payments_monthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="#edf2f7" />
              <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={(v) => `${(v / 1e6).toFixed(1)} млн`} tick={{ fontSize: 11 }} />
              <Tooltip
                labelFormatter={fmtDate}
                formatter={(v: number) => `${v.toLocaleString("uk-UA")} ₴`}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="amount_uah" fill="#805ad5" name="Виплата (₴)" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      {/* 4. ESOZ направлення */}
      {data.referrals_monthly.length > 0 && (
        <ChartCard
          title="ESOZ електронні направлення"
          subtitle={`${data.referrals_monthly.length} місяців · created vs completed = маршрутизація`}
        >
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.referrals_monthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="#edf2f7" />
              <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip labelFormatter={fmtDate} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="created" fill="#3182ce" name="Створено" />
              <Bar dataKey="completed" fill="#38a169" name="Виконано" />
              <Bar dataKey="recalled" fill="#dd6b20" name="Відкликано" />
              <Bar dataKey="error" fill="#e53e3e" name="Помилок" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      {/* 5. Фінансові показники */}
      {data.finance_quarterly.length > 0 && (
        <ChartCard
          title="Фінансові показники (1-нс)"
          subtitle={`${data.finance_quarterly.length} періодів звітності · revenue/expense/assets`}
        >
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.finance_quarterly}>
              <CartesianGrid strokeDasharray="3 3" stroke="#edf2f7" />
              <XAxis dataKey="period" tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={(v) => `${(v / 1e6).toFixed(0)} млн`} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v: number) => v != null ? `${v.toLocaleString("uk-UA")} ₴` : "—"} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="revenue_uah" fill="#38a169" name="Доходи" />
              <Bar dataKey="expense_uah" fill="#e53e3e" name="Витрати" />
              <Bar dataKey="assets_uah" fill="#2c5282" name="Активи" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      )}
    </div>
  );
}

function ChartCard({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div style={{
      background: "white", padding: 16, border: "1px solid #e2e8f0",
      borderRadius: 6, marginBottom: 16,
    }}>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#2c5282" }}>{title}</div>
        {subtitle && <div style={{ fontSize: 11, color: "#a0aec0", marginTop: 2 }}>{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}

function fmtDate(d: string) {
  if (!d) return "";
  // YYYY-MM-DD → MMM YY (uk)
  const [y, m] = d.split("-");
  const months = ["Січ", "Лют", "Бер", "Кві", "Тра", "Чер", "Лип", "Сер", "Вер", "Жов", "Лис", "Гру"];
  return `${months[parseInt(m) - 1] || m} ${y.slice(2)}`;
}

const inputStyle: React.CSSProperties = {
  padding: "5px 8px", fontSize: 12, border: "1px solid #cbd5e0",
  borderRadius: 4, outline: "none",
};
